import {
  type GenerationDetail,
  GenerationDetailSchema
} from '@stratumn/studio-shared';
import { useQuery } from '@tanstack/react-query';

import { generationKeys } from '@/lib/query-keys';
import { HTTPWrapper } from '@/utils/http';

const POLL_INTERVAL = 5000;

const http = new HTTPWrapper<unknown>('process');

const fetchGeneration = async (
  processId: string,
  generationId: string
): Promise<GenerationDetail> => {
  const response = await http.get<unknown>(
    `/${processId}/generations/${generationId}`
  );

  if (response == null) {
    throw new Error(`Generation ${generationId} not found`);
  }
  return GenerationDetailSchema.parse(response);
};

type UseGenerationOptions = {
  processId: string;
  generationId: string;
  enabled?: boolean;
};

type UseGenerationResult = {
  generation: GenerationDetail | undefined;
  isLoading: boolean;
  isError: boolean;
  /**
   * Underlying query error, surfaced so consumers can show retry UI rather
   * than collapsing every failure into a generic "not found" state.
   */
  error: unknown;
  refetch: () => void;
};

export const useGeneration = ({
  processId,
  generationId,
  enabled = true
}: UseGenerationOptions): UseGenerationResult => {
  const query = useQuery({
    queryKey: generationKeys.detail(processId, generationId),
    queryFn: () => fetchGeneration(processId, generationId),
    enabled,
    refetchInterval: queryResult => {
      const data = queryResult.state.data;
      if (!data) {
        return false;
      }
      const isInProgress =
        data.status === 'PENDING' || data.status === 'RUNNING';
      return isInProgress ? POLL_INTERVAL : false;
    }
  });

  return {
    generation: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      query.refetch();
    }
  };
};
